// ============================================================================
// MarketMasterAreaPage — ЗОНА ОБСЛУЖИВАНИЯ В КАБИНЕТЕ МАСТЕРА.
//
// Мастер задаёт географию, по которой ему показывают заявки: страну/регион/
// город/район, координаты и радиус обслуживания. Данные пишутся в ЕГО карточку
// mkt_masters (entity_id = текущий пользователь); сервер проверяет иерархию,
// координаты и радиус (markettake/geo.go).
//
// Правила отбора (SSoT — сервер): пустое гео — неизвестное измерение (заявка
// не отсекается), отсекает только явное несовпадение или расстояние больше
// более строгого из радиусов (мастера и заявки).
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, RefreshCw, Save, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { logErrorToJournal } from '@/lib/errors';
import type { MarketGeoValue } from '@/lib/marketGeo';
import {
  loadMyMarketMaster,
  saveMarketMasterGeo,
  type MarketMasterProfile,
} from '@/lib/marketMasterArea';
import { toMarketFailure, type MarketFailure } from '@/lib/market';
import { MarketGeoFields } from '@/components/market/MarketGeoFields';
import { MarketRatingBadge } from '@/components/market/MarketRatingBadge';
import { MarketError, MarketLoading } from '@/components/market/MarketState';
import { MarketTopBar } from '@/components/market/MarketTopBar';

interface MarketMasterAreaPageProps {
  onBack: () => void;
}

function sameGeo(a: MarketGeoValue, b: MarketGeoValue): boolean {
  return a.countryId === b.countryId
    && a.regionId === b.regionId
    && a.cityId === b.cityId
    && a.areaId === b.areaId
    && a.lat === b.lat
    && a.lon === b.lon
    && a.radiusKm === b.radiusKm;
}

export function MarketMasterAreaPage({ onBack }: MarketMasterAreaPageProps) {
  const userId = String(useAuthStore((s) => s.user?.id || ''));
  const showSuccess = useToastStore((s) => s.showSuccess);
  const [master, setMaster] = useState<MarketMasterProfile | null>(null);
  const [geo, setGeo] = useState<MarketGeoValue | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<MarketFailure | null>(null);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [tick, setTick] = useState(0);
  const masterRef = useRef<MarketMasterProfile | null>(null);

  // Чтение профиля: асинхронная операция эффекта (setState — только в
  // колбэках загрузки, не в синхронной части эффекта).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const next = await loadMyMarketMaster(userId);
        if (cancelled) return;
        masterRef.current = next;
        setMaster(next);
        setGeo(next ? { ...next.geo } : null);
        setFailure(null);
        setSaveError('');
      } catch (e) {
        if (cancelled) return;
        logErrorToJournal(e, 'Кабинет мастера: зона обслуживания');
        if (masterRef.current === null) setFailure(toMarketFailure(e));
        else setSaveError(toMarketFailure(e).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, tick]);

  /** Повторное чтение с явным состоянием загрузки (кнопки «Обновить/Повторить»). */
  const reload = useCallback(() => {
    setFailure(null);
    setSaveError('');
    setLoading(true);
    setTick((n) => n + 1);
  }, []);

  const dirty = useMemo(
    () => !!(master && geo && !sameGeo(master.geo, geo)),
    [geo, master],
  );

  const save = async () => {
    if (!geo) return;
    setSaving(true);
    setSaveError('');
    try {
      await saveMarketMasterGeo(userId, geo);
      // Сохранение подтверждено сервером — обновляем локальное состояние без
      // повторного чтения (его сбой не должен превращать успех в ошибку).
      const saved = { ...geo };
      setMaster((prev) => (prev ? { ...prev, geo: saved } : prev));
      if (masterRef.current) masterRef.current = { ...masterRef.current, geo: saved };
      setGeo(saved);
      showSuccess('Зона обслуживания сохранена');
    } catch (e) {
      logErrorToJournal(e, 'Кабинет мастера: сохранение зоны обслуживания');
      setSaveError(toMarketFailure(e).message || 'Не удалось сохранить зону обслуживания');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !master) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar title="Зона обслуживания" subtitle="Кабинет мастера" onBack={onBack} />
        <div className="flex-1 overflow-y-auto">
          <MarketLoading text="Загружаем профиль мастера…" />
        </div>
      </div>
    );
  }

  if (failure && !master) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar title="Зона обслуживания" subtitle="Кабинет мастера" onBack={onBack} />
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-2xl p-4 sm:p-6">
            <MarketError failure={failure} onRetry={reload} />
          </div>
        </div>
      </div>
    );
  }

  if (!master || !geo) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar title="Зона обслуживания" subtitle="Кабинет мастера" onBack={onBack} />
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-2xl space-y-3 p-4 sm:p-6">
            <div className="rounded-2xl border border-dashed bg-card/50 p-5 text-sm leading-6 text-muted-foreground">
              Профиль мастера не найден. Зона обслуживания доступна только мастерам — карточку
              мастера создаёт оператор маркетплейса.
            </div>
            <Button variant="outline" onClick={reload}>
              <RefreshCw className="mr-1.5 h-4 w-4" />
              Проверить снова
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <MarketTopBar
        title="Зона обслуживания"
        subtitle={master.name ? `Мастер: ${master.name}` : 'Кабинет мастера'}
        onBack={onBack}
        action={(
          <Button
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg px-2 text-xs"
            onClick={reload}
            disabled={loading || saving}
            aria-label="Обновить зону обслуживания"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        )}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-4 p-4 pb-28 sm:p-6">
          <section className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-primary/5 to-background p-4">
            <div className="flex items-start gap-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                <MapPin className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold">Где вы работаете</h2>
                <p className="mt-0.5 text-[11px] leading-5 text-muted-foreground">
                  Заявки в этой зоне показываются в ленте. Радиус ограничивает расстояние:
                  учитывается более строгий из вашего радиуса и радиуса поиска заявки.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {master.verified && (
                    <div className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                      <ShieldCheck className="h-3 w-3" />
                      Мастер проверен
                    </div>
                  )}
                  <MarketRatingBadge rating={master.rating} count={master.reviewsCount} compact />
                </div>
              </div>
            </div>
          </section>

          {saveError && (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-xs leading-5 text-destructive">
              {saveError}
            </div>
          )}

          <div className="rounded-2xl border bg-card p-4">
            <MarketGeoFields
              value={geo}
              onChange={(next) => {
                setGeo(next);
                setSaveError('');
              }}
              radiusLabel="Радиус обслуживания, км"
              radiusHint="Например, 15 — заявки дальше 15 км от ваших координат не предлагаются. Пусто — без ограничения."
              disabled={saving}
            />
          </div>

          <div className="rounded-2xl border border-dashed bg-card/50 px-4 py-3 text-[10px] leading-5 text-muted-foreground">
            Если у заявки гео не заполнено, она не отсекается: неизвестное измерение считается
            совместимым. Гео-справочники (страны/регионы/города/районы) ведутся в базе.
          </div>
        </div>
      </div>

      <div className="shrink-0 border-t bg-background/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex w-full max-w-2xl gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-12 flex-1 rounded-xl"
            onClick={onBack}
            disabled={saving}
          >
            Закрыть
          </Button>
          <Button
            type="button"
            className="h-12 flex-[1.6] rounded-xl"
            onClick={() => void save()}
            disabled={saving || !dirty}
          >
            <Save className="mr-1.5 h-4 w-4" />
            {saving ? 'Сохраняем…' : dirty ? 'Сохранить зону' : 'Сохранено'}
          </Button>
        </div>
      </div>
    </div>
  );
}
